// The cord-pull moment, made visible — and held until the harness rules.
//
// This isn't a 1.5s flash anymore. When the cord pulls, the banner drops
// and STAYS, narrating the proposal through every gate of the pipeline in
// real time: PULL → PROPOSE → TIER → SANDBOX → DEPLOY. It turns green when
// a rule deploys, or red on the exact gate that rejected it — then fades.
//
// The drama is no longer the pull; it's watching the deterministic system
// vet what the AI just handed it. That's the whole thesis, on screen.

import { useEffect, useRef, useState } from "react";
import { useLoopState } from "../lib/use-loop-state";
import type { State } from "../lib/andon-loop";

type Phase = "proposing" | "tier" | "sandbox" | "deploying" | "deployed" | "rejected";

interface Monitor {
  pullSeq: number;
  phase: Phase;
  detail: string;
  /** Which gate failed, when phase === "rejected". */
  failStage: "tier" | "sandbox" | null;
}

// The five gates, in order, as they light up.
const STAGES = ["PULL", "PROPOSE", "TIER", "SANDBOX", "DEPLOY"] as const;

const PHASE_TITLE: Record<Phase, string> = {
  proposing: "ANDON CORD PULLED",
  tier:      "TIER VERIFICATION",
  sandbox:   "SANDBOX REGRESSION",
  deploying: "DEPLOYING",
  deployed:  "RULE DEPLOYED",
  rejected:  "PROPOSAL REJECTED",
};

/** Derive the current lifecycle phase from the tail of the ledger. The
 *  loop is single-threaded (actions await), so everything after the most
 *  recent andon_pulled belongs to that one pull. */
function computeMonitor(state: Readonly<State>): Monitor | null {
  let pull: { seq: number } | null = null;
  for (const e of state.ledger) {
    if (e.kind === "andon_pulled" && (!pull || e.seq > pull.seq)) pull = { seq: e.seq };
  }
  if (!pull) return null;
  const after = state.ledger.filter((e) => e.seq > pull!.seq);
  const find = (k: string) => after.find((e) => e.kind === k);

  const deployed = find("deployed");
  if (deployed) {
    const p = deployed.payload;
    return {
      pullSeq: pull.seq,
      phase: "deployed",
      failStage: null,
      detail: `${p.ruleName} { ${p.predicate} } · password now ${p.lengthAfter} chars`,
    };
  }
  const sandboxReject = find("rejected_by_sandbox");
  if (sandboxReject) {
    return { pullSeq: pull.seq, phase: "rejected", failStage: "sandbox", detail: String(sandboxReject.payload.reason) };
  }
  const tierReject = find("tier_rejected");
  if (tierReject) {
    return { pullSeq: pull.seq, phase: "rejected", failStage: "tier", detail: String(tierReject.payload.reason) };
  }
  if (find("tested")) {
    return { pullSeq: pull.seq, phase: "deploying", failStage: null, detail: "committing the rule and regenerating the password…" };
  }
  if (find("tier_verified")) {
    return { pullSeq: pull.seq, phase: "sandbox", failStage: null, detail: "can the proposed ruleset still produce a password?" };
  }
  const proposed = find("proposed");
  if (proposed) {
    const p = proposed.payload;
    return { pullSeq: pull.seq, phase: "tier", failStage: null, detail: `${p.ruleName} { ${p.predicate} }` };
  }
  return { pullSeq: pull.seq, phase: "proposing", failStage: null, detail: "the AI is choosing the next rule…" };
}

/** Which gate index the current phase is "at" (0-based into STAGES). */
function currentStageIndex(m: Monitor): number {
  switch (m.phase) {
    case "proposing": return 1;
    case "tier":      return 2;
    case "sandbox":   return 3;
    case "deploying": return 4;
    case "deployed":  return 5; // past the last gate → all done
    case "rejected":  return m.failStage === "tier" ? 2 : 3;
  }
}

type PillState = "done" | "active" | "failed" | "pending";

function pillState(m: Monitor, i: number): PillState {
  const cur = currentStageIndex(m);
  if (m.phase === "rejected") {
    if (i === cur) return "failed";
    if (i > cur) return "pending";
    return "done";
  }
  if (i < cur) return "done";
  if (i === cur) return "active";
  return "pending";
}

export function AndonBanner() {
  const state = useLoopState();
  const monitor = computeMonitor(state);
  const [show, setShow] = useState(false);
  const seenSeq = useRef(0);
  const dismissT = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const pullSeq = monitor?.pullSeq ?? 0;
  const phase = monitor?.phase;

  useEffect(() => {
    if (!monitor) return;
    // New cord pull → show and cancel any pending dismiss.
    if (monitor.pullSeq > seenSeq.current) {
      seenSeq.current = monitor.pullSeq;
      setShow(true);
      if (dismissT.current) clearTimeout(dismissT.current);
    }
    // Terminal phase → schedule the fade (rejections linger longer so the
    // reason is readable).
    const terminal = monitor.phase === "deployed" || monitor.phase === "rejected";
    if (terminal && show) {
      if (dismissT.current) clearTimeout(dismissT.current);
      dismissT.current = setTimeout(() => setShow(false), monitor.phase === "rejected" ? 4200 : 2800);
    }
    return () => { if (dismissT.current) clearTimeout(dismissT.current); };
  }, [pullSeq, phase, show, monitor]);

  if (!show || !monitor) return null;

  const tone =
    monitor.phase === "deployed" ? "ab-ok" :
    monitor.phase === "rejected" ? "ab-bad" :
    "ab-busy";

  return (
    <div className={`ab ${tone}`} role="alert" aria-live="assertive">
      <div className="ab-inner">
        <div className="ab-head">
          <span className="ab-cord">⚠</span>
          <span className="ab-text">{PHASE_TITLE[monitor.phase]}</span>
          <span className="ab-detail">{monitor.detail}</span>
        </div>
        <div className="ab-stages">
          {STAGES.map((label, i) => {
            const st = pillState(monitor, i);
            return (
              <div key={label} className={`ab-pill ab-pill-${st}`}>
                <span className="ab-pill-dot" />
                {label}
              </div>
            );
          })}
        </div>
      </div>
      <Styles />
    </div>
  );
}

function Styles() {
  return (
    <style>{`
      .ab {
        position: fixed;
        top: 0; left: 0; right: 0;
        z-index: 100;
        color: #fff;
        padding: var(--space-3) var(--space-4);
        border-bottom: 4px solid var(--fg-0);
        animation: ab-drop 0.35s cubic-bezier(.2,.8,.2,1);
        transition: background 0.4s ease;
      }
      .ab-busy { background: var(--andon-red); }
      .ab-ok   { background: var(--safety-green); }
      .ab-bad  {
        background: var(--andon-red);
        background-image: repeating-linear-gradient(
          45deg, transparent, transparent 12px,
          rgba(0,0,0,0.10) 12px, rgba(0,0,0,0.10) 24px
        );
      }
      .ab-inner {
        max-width: 1400px;
        margin: 0 auto;
        display: flex; flex-direction: column;
        gap: var(--space-2);
      }
      .ab-head {
        display: flex; align-items: baseline; gap: var(--space-3);
      }
      .ab-cord {
        font-size: 22px;
        font-weight: 900;
        animation: ab-shake 0.45s ease;
      }
      .ab-text {
        flex: 0 0 auto;
        font-family: var(--font-display);
        font-weight: 900;
        font-size: 17px;
        letter-spacing: 0.1em;
      }
      .ab-detail {
        flex: 1;
        font-family: var(--font-mono);
        font-size: 12px;
        opacity: 0.95;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .ab-stages {
        display: flex; flex-wrap: wrap; gap: 6px;
      }
      .ab-pill {
        display: inline-flex; align-items: center; gap: 6px;
        font-family: var(--font-mono);
        font-size: 10px;
        font-weight: 800;
        letter-spacing: 0.08em;
        padding: 3px 10px;
        border-radius: 999px;
        border: 1.5px solid rgba(255,255,255,0.45);
        background: rgba(0,0,0,0.12);
        color: rgba(255,255,255,0.75);
      }
      .ab-pill-dot {
        width: 7px; height: 7px;
        border-radius: 50%;
        background: currentColor;
      }
      .ab-pill-pending { opacity: 0.5; }
      .ab-pill-done {
        background: rgba(255,255,255,0.22);
        border-color: rgba(255,255,255,0.85);
        color: #fff;
      }
      .ab-pill-active {
        background: #fff;
        border-color: #fff;
        color: var(--fg-0);
        animation: ab-pulse 0.9s ease-in-out infinite;
      }
      .ab-pill-failed {
        background: var(--fg-0);
        border-color: var(--fg-0);
        color: #fff;
        animation: ab-buzz 0.4s ease 2;
      }
      @keyframes ab-pulse {
        0%, 100% { transform: scale(1);    box-shadow: 0 0 0 0 rgba(255,255,255,0.6); }
        50%      { transform: scale(1.05); box-shadow: 0 0 0 5px rgba(255,255,255,0); }
      }
      @keyframes ab-buzz {
        0%,100% { transform: translateX(0); }
        25% { transform: translateX(-3px); }
        75% { transform: translateX(3px); }
      }
      @keyframes ab-shake {
        0% { transform: translateX(0); }
        15% { transform: translateX(-6px); }
        30% { transform: translateX(6px); }
        45% { transform: translateX(-4px); }
        60% { transform: translateX(4px); }
        75% { transform: translateX(-2px); }
        100% { transform: translateX(0); }
      }
      @keyframes ab-drop {
        from { opacity: 0; transform: translateY(-100%); }
        to   { opacity: 1; transform: translateY(0); }
      }
    `}</style>
  );
}
